import Modal from '../../../components/Modal';
import {
  getScriptFileValidationMessageKey,
  SCRIPT_UPLOAD_ACCEPT,
  validateScriptUploadFile,
} from '../../../utils/scriptFileValidation';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ScriptFormatGuideModalProps {
  open: boolean;
  t: Translate;
  onClose: () => void;
  onSelectFile: (file: File) => void;
  onInvalidFile: (messageKey?: string) => void;
  /** false = 硬强制模式：隐藏 ✕、禁用 Esc（用于新建项目必须上传剧本的 Step4）。默认 true。 */
  dismissible?: boolean;
  /** 强制模式下选文件即直接上传，上传期间置 true：禁用选择按钮并显示「上传中…」。 */
  uploading?: boolean;
}

export default function ScriptFormatGuideModal({
  open,
  t,
  onClose,
  onSelectFile,
  onInvalidFile,
  dismissible = true,
  uploading = false,
}: ScriptFormatGuideModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={dismissible}
      solidBackdrop={!dismissible}
      title={t('formatGuide.title')}
      width="520px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('formatGuide.supportedFormats')}</span>
          <label
            className="btn btn-primary"
            style={{ cursor: uploading ? 'not-allowed' : 'pointer', margin: 0, opacity: uploading ? 0.6 : 1, pointerEvents: uploading ? 'none' : undefined }}
          >
            {uploading ? t('formatGuide.uploading') : t('script.selectFile')}
            <input
              type="file"
              style={{ display: 'none' }}
              accept={SCRIPT_UPLOAD_ACCEPT}
              disabled={uploading}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (file) {
                  const validation = await validateScriptUploadFile(file);
                  if (validation.ok) {
                    onSelectFile(file);
                  } else {
                    onInvalidFile(getScriptFileValidationMessageKey(validation.reason));
                  }
                }
                event.target.value = '';
              }}
            />
          </label>
        </div>
      }
    >
      <div style={{ fontSize: '0.88rem', color: 'var(--text-2)', lineHeight: 1.7 }}>
        <p style={{ margin: '0 0 0.75rem', color: 'var(--text)' }}>
          <span dangerouslySetInnerHTML={{ __html: t('formatGuide.intro') }} />
        </p>

        <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.3rem 0', fontWeight: 600, color: 'var(--text)' }}>{t('formatGuide.formatColumn')}</th>
                <th style={{ textAlign: 'left', padding: '0.3rem 0', fontWeight: 600, color: 'var(--text)' }}>{t('formatGuide.exampleColumn')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['第X集', '第一集、第2集、第十二集'],
                ['第X话', '第一话、第3话'],
                ['第X章', '第一章、第5章'],
                ['EPX / EPISODE X', 'EP01、Episode 1、EP.3'],
              ].map(([format, example], index) => (
                <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.35rem 0', fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--primary)' }}>{format}</td>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-2)' }}>{example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          marginBottom: '0.75rem',
          background: 'var(--surface-2)',
          borderRadius: 'var(--radius)',
          borderLeft: '3px solid var(--primary)',
          padding: '0.6rem 0.85rem',
        }}>
          <div style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.4rem', fontSize: '0.92rem' }}>{t('formatGuide.formatRequirements')}</div>
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.8 }}>
            <li dangerouslySetInnerHTML={{ __html: t('formatGuide.requirement1') }} />
            <li>{t('formatGuide.requirement2')}</li>
            <li>{t('formatGuide.requirement3')}</li>
          </ul>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem', fontSize: '0.85rem' }}>{t('formatGuide.recommendedFormat')}</div>
          <pre style={{
            background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '0.6rem 0.8rem',
            fontSize: '0.8rem', color: 'var(--text-2)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6,
            border: '1px solid var(--border)',
          }}>
{`第一集
场景1：客厅，白天
小明走进房间，看到桌上的信...

第二集
场景1：办公室，清晨
小红打开电脑，收到一封邮件...`}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
